import { Injectable } from "@angular/core";
import { Observable, from } from "rxjs";
import { db, SyncableStatus, UserVendorContact } from "../../../core/database/db";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { LoggerService } from "../../../shared/services/logger.service";
import { isValidEmail } from "../../../shared/utils/email-helper";

export interface UpsertContactResult {
  contact: UserVendorContact;
  upsertResult: "added" | "updated" | "restored";
}

@Injectable({ providedIn: "root" })
export class UserVendorContactService {
  isOnline: boolean = true;

  constructor(
    private logger: LoggerService,
    private networkService: NetworkStatusService,
  ) {
    this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
  }

  getContacts(): Observable<UserVendorContact[]> {
    return from(db.userVendorContact.toArray());
  }

  async addContact(userVendorContact: Omit<UserVendorContact, "id" | "deleted" | "updatedAt" | "syncStatus">): Promise<UserVendorContact> {
    const contactToAdd: Omit<UserVendorContact, "id"> = {
      ...userVendorContact,
      updatedAt: new Date(),
      deleted: false,
      syncStatus: SyncableStatus.Pending
    };

    return db.userVendorContact.add(contactToAdd).then(id => ({ ...contactToAdd, id }));
  }

  async updateContact(contactToUpdate: UserVendorContact, updates: Partial<UserVendorContact>): Promise<UserVendorContact> {
    const updatedContact: UserVendorContact = {
      ...contactToUpdate,
      ...updates,
      deleted: false,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    };

    await db.userVendorContact.update(contactToUpdate.id!, updatedContact);
    return updatedContact;
  }

  async upsertContact(contactToUpsert: UserVendorContact, updates?: Partial<UserVendorContact>): Promise<UpsertContactResult> {
    const existingContactBasedOnUpdatedEmail = (updates && await this.getContactRegardlessOfDeletion(
      contactToUpsert.externalVendorId,
      contactToUpsert.externalFinancialCommunityId,
      updates.email ?? ""
    ));
    const existingContactBasedOnExistingEmail = (await this.getContactRegardlessOfDeletion(
      contactToUpsert.externalVendorId,
      contactToUpsert.externalFinancialCommunityId,
      contactToUpsert.email
    ));

    const existingContact = existingContactBasedOnUpdatedEmail || existingContactBasedOnExistingEmail;
    if (existingContact) {
      const isRestoringExistingDeletedContact = existingContact.deleted;

      let updatedContact: UserVendorContact;
      if (isRestoringExistingDeletedContact) {
        // If the contact still exists in the system as `deleted` we need to effectively "undelete" and update it
        updatedContact = await this.updateContact(existingContact, updates ?? {});
      } else {
        const { id: _id, ...contactDataWithoutId } = contactToUpsert;

        const contactDataToAdd: Omit<UserVendorContact, "id"> = {
          ...contactDataWithoutId,
          ...updates
        };

        // We _have_ to delete and add a contact as an update. This is because we don't have an identifier
        // to match on to do update on the server.
        await this.deleteContact(contactToUpsert.id!);
        updatedContact = await this.addContact(contactDataToAdd);
      }


      return {
        contact: updatedContact,
        upsertResult: isRestoringExistingDeletedContact ? "restored" : "updated"
      };
    }

    const contactToAdd = {
      ...contactToUpsert,
      ...updates,
    };
    const addedContact = await this.addContact(contactToAdd);
    return { contact: addedContact, upsertResult: "added" };
  }

  async deleteContact(id: number): Promise<void> {
    try {
      await db.userVendorContact.update(id, {
        deleted: true,
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending
      });

    } catch (error) {
      this.logger.error("Error deleting contact", error);
      throw error;
    }
  }

  async getContactRegardlessOfDeletion(externalVendorId: number, externalFinancialCommunityId: number, email: string): Promise<UserVendorContact | undefined> {
    return await db.userVendorContact
      .where("externalVendorId")
      .equals(externalVendorId)
      .and(contact =>
        contact.externalFinancialCommunityId === externalFinancialCommunityId
        && contact.email?.trim()?.toLowerCase() === email?.trim()?.toLowerCase()
      )
      .first();
  }

  async getContactsByVendor(vendorId: number): Promise<UserVendorContact[]> {
    return await db.userVendorContact
      .where("externalVendorId")
      .equals(vendorId)
      .toArray();
  }

  async getActiveContactsForVendorInCommunity(
    externalVendorId: number,
    externalFinancialCommunityId: number
  ): Promise<UserVendorContact[]> {
    return await db.userVendorContact
      .where("externalVendorId")
      .equals(externalVendorId)
      .and(contact =>
        !contact.deleted
        && contact.externalFinancialCommunityId === externalFinancialCommunityId
        && !!contact.email?.trim()
      )
      .toArray();
  }

  async getActiveContactsForAllVendorsInCommunity(
    externalFinancialCommunityId: number
  ): Promise<UserVendorContact[]> {
    return await db.userVendorContact
      .filter(contact =>
        !contact.deleted
        && contact.externalFinancialCommunityId === externalFinancialCommunityId
        && !!contact.email?.trim()
      )
      .toArray();
  }
}

export function hasUsableVendorContact(contacts: UserVendorContact[]): boolean {
  return (contacts ?? []).some(contact => !contact?.deleted && isValidEmail(contact?.email ?? ""));
}